import { ActionIcon, Box, Card, Menu, Stack } from "@mantine/core";
import { IconDotsVertical } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { LazyImage } from "../ui";

export interface CardAction {
  label: string;
  icon?: ReactNode;
  color?: string;
  disabled?: boolean;
  onClick: () => void;
}

interface GalleryCardProps {
  src: string;
  alt: string;
  /** Background behind the image while it loads (model colour / bow colour). */
  tint?: string | null;
  onOpen: () => void;
  /** Overlaid in the image's top-left corner (e.g. a status badge). */
  badge?: ReactNode;
  /** Rendered under the image. */
  caption?: ReactNode;
  /** Kebab menu in the image's top-right corner. Nothing is rendered when empty. */
  actions?: CardAction[];
}

/** The one gallery tile: lazy image, optional badge/caption, and a kebab menu with the item's actions. */
export default function GalleryCard({ src, alt, tint, onOpen, badge, caption, actions = [] }: GalleryCardProps) {
  return (
    <Card className="hover-card" padding={0} style={{ height: "100%", overflow: "hidden" }} onClick={onOpen}>
      <Stack gap={0} h="100%">
        <Box style={{ position: "relative" }}>
          <LazyImage src={src} alt={alt} color={tint} />
          {badge && <Box style={{ position: "absolute", top: 8, left: 8 }}>{badge}</Box>}
          {actions.length > 0 && (
            <Box style={{ position: "absolute", top: 4, right: 4 }} onClick={(e) => e.stopPropagation()}>
              <Menu position="bottom-end" withinPortal shadow="md">
                <Menu.Target>
                  <ActionIcon variant="default" size="sm" aria-label="Actions">
                    <IconDotsVertical size={14} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {actions.map((a) => (
                    <Menu.Item key={a.label} leftSection={a.icon} color={a.color} disabled={a.disabled} onClick={a.onClick}>
                      {a.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Box>
          )}
        </Box>
        {caption && (
          <Stack gap={4} p="sm" style={{ flex: 1 }}>
            {caption}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
